import ImportedTechnologyCatalog from "@/components/ImportedTechnologyCatalog";
import LaserSettingsEditor from "./LaserSettingsEditor";
import { useEffect, useMemo, useState } from "react";
import { createSaveQueue } from "@/lib/saveQueue";
import AutosaveChip from "@/components/AutosaveChip";

import { api } from "@/lib/api";
import { dollarsToCents, apiError } from "@/lib/setupValues";
import { fmtCents } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Save, X, Edit2, Trash2 } from "lucide-react";

/** Convert an array of {thickness, feed, pierce, dwell} rows into the map shape
 *  the backend expects. Empty thickness or non-numeric values are silently
 *  dropped — validation is enforced in `save()` before submit.
 */
function rowsToMaps(rows) {
  const speeds = {}, pierces = {}, dwells = {};
  for (const r of rows) {
    const t = String(Number(r.thickness));
    if (!Number.isFinite(Number(r.thickness))) continue;
    if (r.feed !== "" && Number.isFinite(Number(r.feed))) speeds[t] = Number(r.feed);
    if (r.pierce !== "" && Number.isFinite(Number(r.pierce))) pierces[t] = Number(r.pierce);
    if (r.dwell !== "" && Number.isFinite(Number(r.dwell))) dwells[t] = Number(r.dwell);
  }
  return { speeds, pierces, dwells };
}

function mapsToRows(rt) {
  const s = rt?.speeds_by_thickness_mm || {};
  const p = rt?.pierces_by_thickness_mm || {};
  const d = rt?.dwells_by_thickness_mm || {};
  const all = new Set([...Object.keys(s), ...Object.keys(p), ...Object.keys(d)]);
  return [...all].sort((a, b) => Number(a) - Number(b)).map((t) => ({
    id: `${t}-${Math.random().toString(36).slice(2, 7)}`,
    thickness: t, feed: s[t] ?? "", pierce: p[t] ?? "", dwell: d[t] ?? "",
  }));
}

const BLANK_ROW = () => ({ id: `new-${Math.random().toString(36).slice(2, 8)}`, thickness: "", feed: "", pierce: "", dwell: "" });

const EMPTY = {
  machine_id: "",
  name: "",
  processing_class: "laser-fibre",
  applicable_material_families: [],
  laser_hourly_rate_cents: "",
  machine_setup_minutes: "",
  sheet_loading_seconds_per_sheet: "",
  handling_seconds_per_part: "",
  quote_minimum_cents: "",
};

export default function RateTablesPage() {
  const { session } = useAuth();
  const isAdmin = session?.user?.role === "admin";
  const [rates, setRates] = useState([]);
  const [machines, setMachines] = useState([]);
  const [families, setFamilies] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // null | "new" | rate_id
  const [form, setForm] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [familiesText, setFamiliesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m, f] = await Promise.all([api.get("/rate-tables"), api.get("/machines"), api.get("/materials/families")]);
      setFamilies(f.data);
      setRates(r.data); setMachines(m.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, machine_id: "" });
    setRows([BLANK_ROW()]);
    setFamiliesText("");
  };
  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      machine_id: r.machine_id || "",
      name: r.name || "",
      processing_class: r.processing_class || "laser-fibre",
      applicable_material_families: r.applicable_material_families || [],
      laser_hourly_rate_cents: r.laser_hourly_rate_cents == null ? "" : (r.laser_hourly_rate_cents / 100).toFixed(2),
      machine_setup_minutes: r.machine_setup_minutes ?? "",
      sheet_loading_seconds_per_sheet: r.sheet_loading_seconds_per_sheet ?? "",
      handling_seconds_per_part: r.handling_seconds_per_part ?? "",
      quote_minimum_cents: r.quote_minimum_cents == null ? "" : (r.quote_minimum_cents / 100).toFixed(2),
    });
    setRows(mapsToRows(r));
    setFamiliesText((r.applicable_material_families || []).join(", "));
  };
  const cancel = () => setEditing(null);

  const addRow = () => setRows([...rows, BLANK_ROW()]);
  const patchRow = (idx, field, value) => {
    const copy = rows.slice();
    copy[idx] = { ...copy[idx], [field]: value };
    setRows(copy);
  };
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const queue = useMemo(() => createSaveQueue({
    save: async (_, body) => {
      const { data } = await api.patch(`/rate-tables/${editing}`, body);
      setRates(prev => prev.map(rate => rate.id === data.id ? data : rate));
    }, onState: setSaveStatus,
  }), [editing]);
  useEffect(() => () => { queue.flush().catch(() => {}); }, [queue]);
  const save = async (closeEditor = true) => {
    if (!form.machine_id) return toast.error("Machine is required");
    if (!form.name.trim()) return toast.error("Name is required");
    // Every thickness row: thickness+feed+pierce required (dwell optional).
    const thicknesses = new Set();
    for (const [i, r] of rows.entries()) {
      if (r.thickness === "" && r.feed === "" && r.pierce === "" && r.dwell === "") continue;
      if (!Number.isFinite(Number(r.thickness)) || Number(r.thickness) <= 0) {
        return toast.error(`Row ${i + 1}: thickness must be a positive number`);
      }
      if (thicknesses.has(Number(r.thickness))) return toast.error(`Row ${i + 1}: duplicate thickness`);
      thicknesses.add(Number(r.thickness));
      if (r.dwell !== "" && (!Number.isFinite(Number(r.dwell)) || Number(r.dwell) < 0)) return toast.error(`Row ${i + 1}: dwell must be zero or positive`);
      if (r.feed === "" || !Number.isFinite(Number(r.feed)) || Number(r.feed) <= 0) {
        return toast.error(`Row ${i + 1}: feed (mm/min) must be a positive number`);
      }
      if (r.pierce === "" || !Number.isFinite(Number(r.pierce)) || Number(r.pierce) < 0) {
        return toast.error(`Row ${i + 1}: pierce (s) must be >= 0`);
      }
    }
    const usable = rows.filter((r) => r.thickness !== "" && r.feed !== "" && r.pierce !== "");
    if (usable.length === 0) return toast.error("At least one thickness row required");
    const { speeds, pierces, dwells } = rowsToMaps(usable);
    const numOrErr = (v, label) => {
      const n = Number(v);
      if (v === "" || !Number.isFinite(n)) throw new Error(`${label} is required`);
      return n;
    };
    let body;
    try {
      body = {
        machine_id: form.machine_id,
        name: form.name,
        processing_class: form.processing_class,
        applicable_material_families: familiesText.split(",").map((s) => s.trim()).filter(Boolean),
        speeds_by_thickness_mm: speeds,
        pierces_by_thickness_mm: pierces,
        dwells_by_thickness_mm: dwells,
        laser_hourly_rate_cents: dollarsToCents(form.laser_hourly_rate_cents, "Hourly rate"),
        machine_setup_minutes: numOrErr(form.machine_setup_minutes, "Setup minutes"),
        sheet_loading_seconds_per_sheet: numOrErr(form.sheet_loading_seconds_per_sheet, "Sheet loading seconds"),
        handling_seconds_per_part: numOrErr(form.handling_seconds_per_part, "Handling seconds"),
        runtime_multiplier: 1.00,
        quote_minimum_cents: dollarsToCents(form.quote_minimum_cents, "Quote minimum"),
      };
    } catch (e) { return toast.error(e.message); }

    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/rate-tables", body);
        toast.success("Rate table created");
      } else {
        queue.enqueue("rate", body);
        await queue.flush();
      }
      if (closeEditor) { setEditing(null); await load(); }
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Laser rate tables</h1>
        {isAdmin && editing == null && (
          <button
            data-testid="new-rate-btn"
            onClick={startNew}
            disabled={machines.length === 0}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white text-sm px-3 py-1.5 rounded-md"
            title={machines.length === 0 ? "Create a machine first" : ""}
          >
            <Plus className="w-4 h-4" /> New rate table
          </button>
        )}
      </div>




      <ImportedTechnologyCatalog />
      {isAdmin&&rates.some(rate=>rate.is_v1_virtual)&&<section className="mt-5 rounded-xl border bg-white"><LaserSettingsEditor section="pricing"/></section>}
      {editing != null && isAdmin && (
        <div onBlur={(event) => { if (editing !== "new" && event.target.matches("input,select")) save(false); }} data-testid="rate-form" className="mb-6 bg-white border-2 border-slate-900 rounded-lg p-5">
          <h2 className="text-lg font-semibold mb-4">{editing === "new" ? "New rate table" : "Edit rate table"}</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <Field label="Machine">
              <select
                data-testid="rate-machine-select"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                value={form.machine_id}
                onChange={(e) => setForm({ ...form, machine_id: e.target.value })}
              >
                <option value="">— choose —</option>
                {machines.map((m) => {
                  const label = `${m.name} (${m.power_kw} kW${m.enabled ? "" : ", disabled"})`;
                  return <option key={m.id} value={m.id}>{label}</option>;
                })}
              </select>
            </Field>
            <Field label="Rate name">
              <input data-testid="rate-name-input"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Processing class">
              <input data-testid="rate-class-input"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                value={form.processing_class}
                onChange={(e) => setForm({ ...form, processing_class: e.target.value })} />
            </Field>
            <Field label="Applicable material families">
              <select multiple data-testid="rate-families-input" className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                value={familiesText.split(',').map(s => s.trim()).filter(Boolean)}
                onChange={e => setFamiliesText(Array.from(e.target.selectedOptions, option => option.value).join(', '))}>
                {families.map(family => <option key={family.id} value={family.name}>{family.name}</option>)}
              </select>
            </Field>
          </div>

          {/* Row-based thickness map editor */}
          <div className="border border-slate-200 rounded-md overflow-hidden mb-4">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-medium">Per-thickness rows</div>
              <button data-testid="add-thickness-row" onClick={addRow} type="button"
                className="text-xs inline-flex items-center gap-1 bg-slate-900 text-white px-2 py-1 rounded hover:bg-slate-800">
                <Plus className="w-3 h-3" /> Add row
              </button>
            </div>
            <table data-testid="rate-rows-table" className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-1.5">Thickness (mm)</th>
                  <th className="px-3 py-1.5">Feed (mm/min)</th>
                  <th className="px-3 py-1.5">Pierce (s)</th>
                  <th className="px-3 py-1.5">Dwell (s, optional)</th>
                  <th className="px-3 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-3 text-slate-500 text-center text-xs">No rows — click "Add row"</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`rate-row-editor-${i}`} className="border-t border-slate-100">
                    <RowCell testid={`row-thickness-${i}`} value={r.thickness} step="0.01" onChange={(v) => patchRow(i, "thickness", v)} />
                    <RowCell testid={`row-feed-${i}`} value={r.feed} step="1" onChange={(v) => patchRow(i, "feed", v)} />
                    <RowCell testid={`row-pierce-${i}`} value={r.pierce} step="0.01" onChange={(v) => patchRow(i, "pierce", v)} />
                    <RowCell testid={`row-dwell-${i}`} value={r.dwell} step="0.01" onChange={(v) => patchRow(i, "dwell", v)} />
                    <td className="px-2 py-1">
                      <button data-testid={`remove-row-${i}`} onClick={() => removeRow(i)} type="button"
                        className="text-rose-600 hover:bg-rose-50 rounded p-1" title="Remove row">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <Field label="Hourly rate ($ / hour)">
              <input data-testid="rate-hourly-input" type="number" step="0.01" min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm numeric"
                value={form.laser_hourly_rate_cents}
                onChange={(e) => setForm({ ...form, laser_hourly_rate_cents: e.target.value })}
                placeholder="Enter hourly rate" />
            </Field>
            <Field label="Setup (minutes)">
              <input data-testid="rate-setup-input" type="number" step="0.1" min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm numeric"
                value={form.machine_setup_minutes}
                onChange={(e) => setForm({ ...form, machine_setup_minutes: e.target.value })} />
            </Field>
            <Field label="Sheet loading (seconds per charged sheet)">
              <input data-testid="rate-loading-input" type="number" step="1" min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm numeric"
                value={form.sheet_loading_seconds_per_sheet}
                onChange={(e) => setForm({ ...form, sheet_loading_seconds_per_sheet: e.target.value })}
                placeholder="0 accepted; blank blocks" />
            </Field>
            <Field label="Handling (s per part)">
              <input data-testid="rate-handling-input" type="number" step="1" min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm numeric"
                value={form.handling_seconds_per_part}
                onChange={(e) => setForm({ ...form, handling_seconds_per_part: e.target.value })} />
            </Field>
            <Field label="Quote minimum ($)">
              <input data-testid="rate-minimum-input" type="number" step="0.01" min="0"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm numeric"
                value={form.quote_minimum_cents}
                onChange={(e) => setForm({ ...form, quote_minimum_cents: e.target.value })} />
            </Field>
          </div>
          <div className="flex gap-2 mt-5">
            <AutosaveChip status={saveStatus} />
            <button data-testid="save-rate-btn" onClick={() => save(true)} disabled={saving}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-md">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : editing === "new" ? "Create rate table" : "Done"}
            </button>
            <button data-testid="cancel-rate-btn" onClick={cancel} disabled={saving || saveStatus === "error"}
              className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5 rounded-md">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {machines.length === 0 && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
          No machines yet — <a href="/machines" className="underline font-medium">create a machine</a> before adding a rate table.
        </div>
      )}
      {loading && <div className="text-slate-500 text-sm">loading…</div>}
      <div className="space-y-4">
        {rates.filter(r=>!r.is_v1_virtual).map((r) => {
          const speedRows = r.speeds_by_thickness_mm ? Object.keys(r.speeds_by_thickness_mm) : [];
          const pierceRows = r.pierces_by_thickness_mm ? Object.keys(r.pierces_by_thickness_mm) : [];
          const isVirtual = r.is_v1_virtual === true;
          const missing = !isVirtual && (speedRows.length === 0 || pierceRows.length === 0);
          const machine = machines.find((m) => m.id === r.machine_id);
          return (
            <div key={r.id} data-testid={`rate-row-${r.id}`} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{r.name}</h2>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Machine: <span className="font-medium text-slate-700">{machine ? `${machine.name} · ${machine.power_kw} kW` : (r.machine_name || "—")}</span>
                    <span className="mx-2 text-slate-300">·</span>
                    Class: <span className="font-medium text-slate-700">{r.processing_class || "—"}</span>
                    <span className="mx-2 text-slate-300">·</span>
                    Families: <span className="font-medium text-slate-700">{(r.applicable_material_families || []).join(", ") || "—"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-slate-600">Quote min (AUD): <span className="numeric font-semibold text-slate-900">{fmtCents(r.quote_minimum_cents)}</span></div>
                  {isAdmin && !isVirtual && (
                    <button
                      data-testid={`edit-rate-${r.id}`}
                      onClick={() => startEdit(r)}
                      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 inline-flex items-center gap-1"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                  )}
                </div>
              </div>
              {isVirtual && (
                <div data-testid={`rate-dynamic-${r.id}`} className="mb-3 text-sm bg-amber-50 border border-amber-300 text-amber-900 rounded px-3 py-2">

                </div>
              )}
              {missing && (
                <div data-testid={`rate-not-usable-${r.id}`} className="mb-3 text-sm bg-rose-50 border border-rose-300 text-rose-800 rounded px-3 py-2">
                  Not usable — this rate is missing per-thickness rows. Pricing will block for every sheet.
                </div>
              )}
              {!isVirtual && <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <Stat label="Hourly rate (@ runtime ×1.00)" value={`${fmtCents(r.laser_hourly_rate_cents)}/hr`} />
                <Stat label="Runtime multiplier" value={r.runtime_multiplier ?? 1.0} />
                <Stat label="Setup" value={`${r.machine_setup_minutes} min / group`} />
                <Stat label="Sheet loading" value={r.sheet_loading_seconds_per_sheet == null ? "— (unset, blocks)" : `${r.sheet_loading_seconds_per_sheet} s / charged sheet`} />
                <Stat label="Handling" value={`${r.handling_seconds_per_part} s / part`} />
              </div>}
              {!isVirtual && <RateMatrix rate={r} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="numeric text-sm font-medium text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function RowCell({ testid, value, step, onChange }) {
  return (
    <td className="px-2 py-1">
      <input
        data-testid={testid}
        type="number"
        step={step}
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 border border-slate-300 rounded text-sm numeric text-right"
      />
    </td>
  );
}

function RateMatrix({ rate }) {
  const speeds = rate.speeds_by_thickness_mm || {};
  const pierces = rate.pierces_by_thickness_mm || {};
  const dwells = rate.dwells_by_thickness_mm || {};
  const all = [...new Set([...Object.keys(speeds), ...Object.keys(pierces), ...Object.keys(dwells)])]
    .sort((a, b) => Number(a) - Number(b));
  if (all.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-sm font-medium text-slate-800 mb-2">Per-thickness rows</div>
      <table data-testid={`rate-matrix-${rate.id}`} className="w-full text-sm border border-slate-200">
        <thead className="text-left text-xs text-slate-500 bg-slate-50">
          <tr>
            <th className="px-2 py-1.5">Thickness</th>
            <th className="px-2 py-1.5 text-right">Feed (mm/min)</th>
            <th className="px-2 py-1.5 text-right">Pierce (s)</th>
            <th className="px-2 py-1.5 text-right">Dwell (s)</th>
          </tr>
        </thead>
        <tbody>
          {all.map((t) => (
            <tr key={t} className="border-t border-slate-100">
              <td className="px-2 py-1.5 numeric">{t} mm</td>
              <td className="px-2 py-1.5 numeric text-right">{speeds[t] ?? "—"}</td>
              <td className="px-2 py-1.5 numeric text-right">{pierces[t] ?? "—"}</td>
              <td className="px-2 py-1.5 numeric text-right text-slate-500">{dwells[t] ?? "0"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
